import { inject, Injectable } from "@angular/core";
import { ACCESS_TOKEN_KEY, EXPIRESIN, REFRESH_TOKEN_KEY, ROLES } from "@constants/common.constants";
import { RolesService } from "@modules/home/services/roles.service";
import { StorageService } from "@services/storage/storage.service";
import { BehaviorSubject } from "rxjs";

export interface AuthData {
    accessToken: string;
    refreshToken: string;
    expiryTime: number;
    empNo: string;
    fullName: string;
    email: string;
    userName: string;
    seq: number;
    roles: string[];
    userType: string | null;
    switchedUser: boolean;
    originalUserName: string | null;
  }

  
export interface Pin {
  userPin: string;
}

  @Injectable({
    providedIn: 'root',
  })
  export class AuthService {
    private storageKey = 'authData';
    private pinStorageKey = 'authData_user_pin';
    private readonly storageSvc: StorageService = inject(StorageService);
    public readonly roleSvc:RolesService = inject(RolesService);
    authDatas$ = new BehaviorSubject<string | undefined>('');
  
    // Store the data in localStorage
    setAuthData(data: AuthData): void {
        this.storageSvc.setLocalData(this.storageKey, JSON.stringify(data));
        this.storageSvc.setSessionData(ACCESS_TOKEN_KEY, data.accessToken);        
        this.storageSvc.setSessionData(REFRESH_TOKEN_KEY, data.refreshToken);
        this.storageSvc.setSessionData(ROLES, JSON.stringify(data.roles));
        this.roleSvc.setUserRoles(data?.roles);  
        this.roleSvc.updateRoles(data?.roles);       
        this.authDatas$.next(JSON.stringify(data))     
    }
  
    // Retrieve the stored data
    getAuthData(): AuthData | null {
      const data = this.storageSvc.getLocalData(this.storageKey);
      return data ? (JSON.parse(data) as AuthData) : null;
    }

    // Store the pin in localStorage
    setUserPin(data: Pin): void {      
      this.storageSvc.setLocalData(this.pinStorageKey, JSON.stringify(data));
    }
  
    // Retrieve the pin data
    getUserPin(): Pin | null {
      const data = this.storageSvc.getLocalData(this.pinStorageKey);
      return data ? (JSON.parse(data) as Pin) : null;
    }
  
    // Retrieve a specific property (e.g., accessToken)
    getAuthProperty<T extends keyof AuthData>(property: T): AuthData[T] | null {
      const authData = this.getAuthData();
      return authData ? authData[property] : null;
    }
  
    // Check if the user is authenticated (based on accessToken and expiryTime)
    isAuthenticated(): boolean {
      const authData = this.getAuthData();
      if (authData && authData.accessToken && authData.expiryTime) {
        const currentTime = new Date().getTime();
        return currentTime < authData.expiryTime; // Token not expired
      }
      return false;
    }
  
    // Clear the stored data
    clearAuthData(): void {
      localStorage.removeItem(this.storageKey);
    }
  }